import { MessageId } from "@cleo/contracts";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import ChatView from "../components/ChatView";
import { threadHasStarted } from "../components/ChatView.logic";
import { LocalView } from "../components/LocalView";
import { useWorldView } from "../neucleos/worldView";
import {
  finalizePromotedDraftThreadByRef,
  useComposerDraftSessionStore,
} from "../composerDraftStore";
import { resolveThreadRouteRef, resolveThreadRouteRenderState } from "../threadRoutes";
import { resolveThreadSyncPhase } from "../threadSync";
import {
  useEnvironmentThreadRefs,
  useThreadDetail,
  useThreadShell,
  useThreadStatus,
} from "../state/entities";
import { useEnvironmentQuery } from "../state/query";
import { environmentShell } from "../state/shell";

function ChatThreadRouteView() {
  const navigate = useNavigate();
  const { a, m } = Route.useSearch();
  const { state: sessionRoom } = useWorldView();
  const threadRef = Route.useParams({
    select: (params) => resolveThreadRouteRef(params),
  });
  const shell = useEnvironmentQuery(
    threadRef === null ? null : environmentShell.stateAtom(threadRef.environmentId),
  );
  const serverThreadShell = useThreadShell(threadRef);
  const serverThreadDetail = useThreadDetail(threadRef);
  const serverThreadStatus = useThreadStatus(threadRef);
  const environmentThreadRefs = useEnvironmentThreadRefs(threadRef?.environmentId ?? null);
  const bootstrapComplete = shell.data?.snapshot._tag === "Some";
  const environmentHasServerThreads = environmentThreadRefs.length > 0;
  const draftThreadExists = useComposerDraftSessionStore((store) =>
    threadRef ? store.getDraftThreadByRef(threadRef) !== null : false,
  );
  const draftThread = useComposerDraftSessionStore((store) =>
    threadRef ? store.getDraftThreadByRef(threadRef) : null,
  );
  const environmentHasDraftThreads = useComposerDraftSessionStore((store) => {
    if (!threadRef) {
      return false;
    }
    return store.hasDraftThreadsInEnvironment(threadRef.environmentId);
  });
  const renderState = resolveThreadRouteRenderState({
    bootstrapComplete,
    serverThreadShellExists: serverThreadShell !== null,
    serverThreadDetailExists: serverThreadDetail !== null,
    serverThreadDetailDeleted: serverThreadStatus === "deleted",
    draftThreadExists,
  });
  const threadSyncPhase = resolveThreadSyncPhase({
    detailExists: serverThreadDetail !== null,
    shellExists: serverThreadShell !== null,
    status: serverThreadStatus,
  });
  const serverThreadStarted = threadHasStarted(serverThreadDetail);
  const environmentHasAnyThreads = environmentHasServerThreads || environmentHasDraftThreads;

  useEffect(() => {
    if (!threadRef || !bootstrapComplete) {
      return;
    }

    if (renderState === "missing" && environmentHasAnyThreads) {
      void navigate({ to: "/", replace: true });
    }
  }, [bootstrapComplete, environmentHasAnyThreads, navigate, renderState, threadRef]);

  useEffect(() => {
    if (!threadRef || !serverThreadStarted || !draftThread) {
      return;
    }
    finalizePromotedDraftThreadByRef(threadRef);
  }, [draftThread, serverThreadStarted, threadRef]);

  if (!threadRef) {
    return null;
  }

  if (renderState !== "ready" && !(renderState === "loading" && serverThreadShell !== null)) {
    return null;
  }
  const chatView = (
    <ChatView
      environmentId={threadRef.environmentId}
      threadId={threadRef.threadId}
      routeKind="server"
      threadSyncPhase={threadSyncPhase}
      focusMessageId={m === undefined ? null : MessageId.make(m)}
    />
  );
  // The Local room (principal directive 2026-08-27): an ungoverned session
  // leaf in Local mode wears the session view's grammar — the Cleo Panel
  // seats this session's chat and the Canvas seats its execution surfaces.
  // Everything else (a governed World's session, a shell not yet folded into
  // the local tree) keeps the classic full-width view.
  // The _chat layout owns the SidebarInset frame and the root-session strip;
  // this leaf supplies only its content (PLAN.md Arc A1).
  if (
    sessionRoom.kind === "local" &&
    serverThreadShell !== null &&
    serverThreadShell.worldKey == null
  ) {
    return (
      <LocalView
        addressSessionId={a}
        environmentId={threadRef.environmentId}
        threadId={threadRef.threadId}
        fallback={chatView}
      />
    );
  }
  return chatView;
}

export const Route = createFileRoute("/_chat/$environmentId/$threadId")({
  component: ChatThreadRouteView,
  // `a`: the session a Local room addresses. `m`: the message to open at (W-1efa94b1, a fork's source).
  validateSearch: (search: Record<string, unknown>): { a?: string; m?: string } => ({
    ...(typeof search["a"] === "string" && search["a"].length > 0 ? { a: search["a"] } : {}),
    ...(typeof search["m"] === "string" && search["m"].length > 0 ? { m: search["m"] } : {}),
  }),
});
